import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { SiteService } from '@api/site.service';
import { SiteStateService } from '@states/site-state.service';
import { UserMenuService } from '@api/user-menu.service';
import { LoggerHealthVM, MonitoringSourceType } from '@models/backend-generated-models/models';
import { Subject, Subscription, forkJoin, of } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { CustomerMeterRow, ReviewableGroupVM, UnderlyingMeasurementVM } from './modal-logger-health-review.types';

@Component({
  selector: 'app-modal-logger-health',
  templateUrl: './modal-logger-health.component.html',
  styleUrls: ['./modal-logger-health.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ModalLoggerHealthComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  // Guards against an older site's response overwriting the currently selected site's data
  private loadSubscription: Subscription | null = null;
  private loadRequestSeq: number = 0;

  // State flags
  loading: boolean = false;
  error: string | null = null;
  isSuperAdmin: boolean = false;
  isGlobalView: boolean = false;
  showCustomerView: boolean = true;
  activeTab: 'MAINTENANCE' | 'SUPPORT' = 'MAINTENANCE';
  currentSiteId: number = 0;
  currentSiteName: string = '';

  // Review actions state tracking
  activeDropdownLogger: CustomerMeterRow | null = null;
  activeReviewLogger: CustomerMeterRow | null = null;
  savingReview: boolean = false;
  reviewError: string | null = null;
  successMessage: string | null = null;
  activeContributorFilterIds: number[] | null = null;
  activeContributorParentName: string = '';

  // Data stores
  allLoggers: LoggerHealthVM[] = [];
  liveDataConfig: any[] = [];
  latestValuesData: any[] = [];
  filteredLoggers: CustomerMeterRow[] = [];
  allSiteLiveMeters: CustomerMeterRow[] = [];

  // Section collapse state
  // Collapsed by default; only the user's own click expands it
  needsAttentionSectionExpanded: boolean = false;
  healthySectionExpanded: boolean = true;
  staticSectionExpanded: boolean = false;
  private healthySectionManuallyToggled: boolean = false;

  // Filter chips state
  activeFilterChip: 'ALL' | 'NEEDS_ATTENTION' | 'HEALTHY' | 'STATIC' = 'ALL';

  // Global Support selection state
  selectedSupportLogger: CustomerMeterRow | null = null;
  activeSupportTab: 'OVERVIEW' | 'DIAGNOSTICS' = 'OVERVIEW';

  // Summary counts
  totalCount: number = 0;
  healthyCount: number = 0;
  warningCount: number = 0;
  pipelineCount: number = 0;
  staticCount: number = 0;

  // Filter bindings
  searchTerm: string = '';
  // Sorting state
  sortColumn: string = 'updatedDate';
  sortDirection: 'asc' | 'desc' = 'desc';

  vendorFilter: string = 'ALL';
  statusFilter: string = 'ALL';
  activeKpiFilter: string = 'NONE';
  activityFilter: string = 'ALL';

  constructor(
    public dialogRef: MatDialogRef<ModalLoggerHealthComponent>,
    private siteService: SiteService,
    private siteStateService: SiteStateService,
    private userMenuService: UserMenuService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.checkUserRole();
    if (this.isSuperAdmin) {
      this.activeTab = 'MAINTENANCE';
      this.isGlobalView = false;
      this.showCustomerView = true;
    } else {
      this.error = 'Unauthorized access to Ingestion Health Dashboard.';
    }
    this.trackSelectedSite();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Parse JWT token locally to extract superadmin status
  private checkUserRole(): void {
    const token = localStorage.getItem('jwt');
    if (!token) {
      this.isSuperAdmin = false;
      this.showCustomerView = true;
      return;
    }
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const roleClaim = payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] || payload['role'];
      if (!roleClaim) {
        this.isSuperAdmin = false;
        this.showCustomerView = true;
        return;
      }
      if (Array.isArray(roleClaim)) {
        this.isSuperAdmin = roleClaim.some(r => typeof r === 'string' && r.toLowerCase().includes('superadmin'));
      } else if (typeof roleClaim === 'string') {
        this.isSuperAdmin = roleClaim.toLowerCase().includes('superadmin');
      } else {
        this.isSuperAdmin = false;
      }
      this.showCustomerView = true;
    } catch (e) {
      this.isSuperAdmin = false;
      this.showCustomerView = true;
    }
  }

  // Track the active site selected in the navbar
  private trackSelectedSite(): void {
    this.siteStateService.selectedSite$
      .pipe(takeUntil(this.destroy$))
      .subscribe(site => {
        if (site) {
          this.currentSiteId = site.siteId || 0;
          this.currentSiteName = site.name || 'Selected Site';
          this.activeContributorFilterIds = null;
          this.activeContributorParentName = '';

          // If no site is selected (0), default to global support view
          if (this.currentSiteId === 0) {
            this.activeTab = 'SUPPORT';
            this.isGlobalView = true;
            this.showCustomerView = false;
          } else {
            this.activeTab = 'MAINTENANCE';
            this.isGlobalView = false;
            this.showCustomerView = true;
          }

          this.healthySectionManuallyToggled = false;
          this.needsAttentionSectionExpanded = false;
          this.activeFilterChip = 'ALL';
          this.loadData();
        }
      });
  }

  // Fetch telemetry status rows from WebAPI endpoint
  loadData(): void {
    // Only the authorization failure is terminal; a previous load error must not block a new attempt.
    if (!this.isSuperAdmin) {
      this.loading = false;
      this.cdr.markForCheck();
      return;
    }
    this.loading = true;
    this.error = null;
    this.cdr.markForCheck();

    const targetSiteId = this.isGlobalView ? 0 : this.currentSiteId;

    // Cancel any in-flight load and tag this one so a late response for a previous site is ignored.
    this.loadSubscription?.unsubscribe();
    const requestSeq = ++this.loadRequestSeq;

    // Health is required; config and latest readings are ancillary and fall back to empty.
    this.loadSubscription = forkJoin({
      health: this.siteService.getLoggerHealth$(targetSiteId),
      config: targetSiteId > 0
        ? this.siteService.deepLoadSite$(targetSiteId).pipe(catchError(err => {
            console.warn('Logger Health: site configuration unavailable; continuing without it.', err);
            return of([]);
          }))
        : of([]),
      latestValues: targetSiteId > 0
        ? this.userMenuService.getLatestReadings$(targetSiteId).pipe(catchError(err => {
            console.warn('Logger Health: latest readings unavailable; continuing without them.', err);
            return of([]);
          }))
        : of([])
    })
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: ({ health, config, latestValues }) => {
        if (requestSeq !== this.loadRequestSeq) return;
        this.allLoggers = this.mergeDuplicateLoggers(health || []);
        this.liveDataConfig = config || [];
        this.latestValuesData = latestValues || [];
        this.applyFilters();
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        if (requestSeq !== this.loadRequestSeq) return;
        this.error = 'Failed to fetch logger health data from backend server. Please verify WebAPI status.';
        this.loading = false;
        this.cdr.markForCheck();
      }
    });
  }

  // Switch between Maintenance (customer-friendly) and Support (technical diagnostics) views
  switchTab(tab: 'MAINTENANCE' | 'SUPPORT'): void {
    if (this.activeTab === tab) return;
    if (tab === 'SUPPORT' && !this.isSuperAdmin) return;

    this.activeTab = tab;
    this.isGlobalView = (tab === 'SUPPORT');
    this.showCustomerView = (tab === 'MAINTENANCE');

    // Reset filter states for view consistency
    this.searchTerm = '';
    this.vendorFilter = 'ALL';
    this.statusFilter = 'ALL';
    this.activeKpiFilter = 'NONE';
    this.activeFilterChip = 'ALL';
    this.activityFilter = 'ALL';
    this.activeContributorFilterIds = null;
    this.activeContributorParentName = '';
    this.healthySectionManuallyToggled = false;
    this.needsAttentionSectionExpanded = false;

    this.selectedSupportLogger = null;
    this.loadData();
    this.cdr.markForCheck();
  }

  selectSupportLogger(logger: CustomerMeterRow): void {
    this.selectedSupportLogger = logger;
    this.cdr.markForCheck();
  }

  inspectOverview(logger: CustomerMeterRow, event?: MouseEvent): void {
    if (event) event.stopPropagation();
    this.selectedSupportLogger = logger;
    this.activeSupportTab = 'OVERVIEW';
    this.cdr.markForCheck();
  }

  inspectDiagnostics(logger: CustomerMeterRow, event?: MouseEvent): void {
    if (event) event.stopPropagation();
    this.selectedSupportLogger = logger;
    this.activeSupportTab = 'DIAGNOSTICS';
    this.cdr.markForCheck();
  }

  closeSupportDetails(): void {
    this.selectedSupportLogger = null;
    this.cdr.markForCheck();
  }

  setSupportTab(tab: 'OVERVIEW' | 'DIAGNOSTICS'): void {
    this.activeSupportTab = tab;
    this.cdr.markForCheck();
  }


  setSortColumn(col: string) {
    if (this.sortColumn === col) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = col;
      this.sortDirection = col === 'updatedDate' || col === 'lastReading' ? 'desc' : 'asc';
    }
    this.applyFilters();
  }

  // Toggle detail rows in Customer View
  toggleDetails(logger: CustomerMeterRow): void {
    if (this.showCustomerView) {
      logger.detailsExpanded = !logger.detailsExpanded;
      this.cdr.markForCheck();
    }
  }

  toggleSupportDetails(logger: any): void {
    if (!this.isSuperAdmin) return;
    logger.detailsExpanded = !logger.detailsExpanded;
    this.cdr.markForCheck();
  }

  toggleTechnicalDrawer(logger: CustomerMeterRow, event: MouseEvent): void {
    event.stopPropagation();
    logger.supportDetailsExpanded = !logger.supportDetailsExpanded;
    this.cdr.markForCheck();
  }

  // Authoritative friendly name ordering helper
  getMeterName(logger: any): string {
    if (logger.meterName && logger.meterName.trim() !== '') {
      return logger.meterName;
    }
    if (logger.configuredName && logger.configuredName.trim() !== '') {
      return logger.configuredName;
    }
    if (logger.configDescription && logger.configDescription.trim() !== '') {
      return logger.configDescription;
    }
    if (logger.loggerName && logger.loggerName.trim() !== '') {
      return logger.loggerName;
    }
    return 'Meter description unavailable';
  }

  // Get genuine friendly meter subtitle for Global Support site column (returns null if redundant or raw key)
  getFriendlyMeterSubtitle(logger: any): string | null {
    const meter = (logger.meterName || logger.configuredName || logger.configDescription || '').trim();
    if (!meter || meter === 'Meter description unavailable') return null;
    const site = (logger.siteName || '').trim().toLowerCase();
    const key = (logger.sourceDeviceKey || '').trim().toLowerCase();
    const meterLower = meter.toLowerCase();
    if (meterLower === site || meterLower === key) return null;
    return meter;
  }


  // Authoritative Type helper
  getMeterType(logger: any): string {
    const raw = (logger.fpCategoryName || logger.categoryName || logger.type || '').trim();
    const cat = raw.toLowerCase();
    const rt = (logger.configReadingType || '').trim().toUpperCase();

    if (cat.includes('electricity') || cat.includes('power') || rt.startsWith('E') || rt === 'P' || rt === 'C') {
      return 'Electricity';
    }
    if (cat.includes('gas') || rt.startsWith('G')) {
      return 'Gas';
    }
    if (cat.includes('water') || rt.startsWith('W') || rt === 'F') {
      return 'Water';
    }
    if (cat.includes('solar')) {
      return 'Solar PV';
    }
    if (cat.includes('heat') || cat.includes('chp')) {
      return 'Heat';
    }
    if (cat.includes('steam')) {
      return 'Steam';
    }
    if (cat.includes('oil')) {
      return 'Oil';
    }
    if (cat.includes('biomass')) {
      return 'Biomass';
    }
    if (cat.includes('temperature') || rt === 'T') {
      return 'Temperature';
    }
    if (cat.includes('humidity') || rt === 'H') {
      return 'Humidity';
    }
    if (cat.includes('voltage') || rt === 'U') {
      return 'Voltage';
    }
    if (cat.includes('current') || rt === 'I') {
      return 'Current';
    }

    if (raw) {
      return raw;
    }
    return 'General';
  }

  // Physical address helper
  getPhysicalAddress(logger: any): string | null {
    if (logger.sourceDeviceKey) {
      const parts = logger.sourceDeviceKey.split('-');
      if (parts.length >= 3) return parts[1];
      if (parts.length === 2) return parts[0];
    }
    return logger.meterNameConfig || logger.meterName || null;
  }

  // Masked physical address helper (showing only last 4 chars for privacy)
  getPhysicalAddressMasked(logger: any): string {
    const physical = this.getPhysicalAddress(logger);
    if (!physical) return 'Source details unavailable';
    return physical.length > 4 ? `...${physical.substring(physical.length - 4)}` : physical;
  }

  // Short customer-friendly Meter Reference helper
  getMeterReference(logger: any): string {
    if (logger.status === 'Static Data' || logger.monitoringSourceType === 2 || logger.isStaticSection) {
      return 'Not live monitored';
    }

    if (logger.underlyingMeasurements && logger.underlyingMeasurements.length > 0) {
      const count = logger.underlyingMeasurements.length;
      return count === 1 ? '1 register channel' : `${count} register channels`;
    }

    const physical = this.getPhysicalAddress(logger);
    if (!physical) return 'Source details unavailable';
    const shortSuffix = physical.length > 4 ? physical.substring(physical.length - 4) : physical;
    return `Meter ...${shortSuffix}`;
  }

  // Helper to calculate customer-friendly Data Age based on Last Reading timestamp
  getDataAge(logger: any): string {
    if (logger.status === 'Static Data' || logger.monitoringSourceType === 2 || logger.isStaticSection) {
      return 'Not applicable';
    }
    const timeVal = logger.lastReading || this.getLatestReadingTime(logger);
    if (!timeVal) return 'No reading available';

    try {
      const readingDate = new Date(timeVal);
      if (isNaN(readingDate.getTime())) return 'No reading available';

      const now = new Date();
      const diffMs = now.getTime() - readingDate.getTime();

      if (diffMs <= 60000) {
        return 'Just now';
      }

      const diffMins = Math.floor(diffMs / 60000);
      if (diffMins < 60) {
        return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
      }

      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) {
        return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
      }

      const diffDays = Math.floor(diffHours / 24);
      return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
    } catch (e) {
      return 'No reading available';
    }
  }

  getLatestValue(logger: any): number | null {
    if (logger.latestValue !== undefined && logger.latestValue !== null) {
      return logger.latestValue;
    }
    return null;
  }

  getLatestValueDisplay(logger: any): string {
    const val = this.getLatestValue(logger);
    if (val === null || val === undefined) {
      return '\u2014';
    }
    const unit = logger.unit || this.getLatestValueUnit(logger);
    return `${val.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ${unit}`.trim();
  }

  formatLatestValue(logger: any): string {
    const val = this.getLatestValue(logger);
    if (val === null || val === undefined) {
      return '\u2014';
    }
    return val.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  }

  getLatestValueUnit(logger: any): string {
    return logger.unit || logger.configUnit || '';
  }

  getLatestReadingTime(logger: any): any {
    if (logger.lastReading) {
      return logger.lastReading;
    }
    if (logger.latestRawReadingDate) {
      return logger.latestRawReadingDate;
    }
    if (logger.latestAggregatedReadingDate) {
      return logger.latestAggregatedReadingDate;
    }
    return null;
  }

  // Returns the chronologically newest valid timestamp of the two (or whichever one exists)
  private newestDate<T extends Date | string | null | undefined>(a: T, b: T): T {
    const ta = a ? new Date(a as any).getTime() : NaN;
    const tb = b ? new Date(b as any).getTime() : NaN;
    if (isNaN(ta)) return isNaN(tb) ? a : b;
    if (isNaN(tb)) return a;
    return tb > ta ? b : a;
  }

  // Merge duplicate legacy keys with current TRV keys
  mergeDuplicateLoggers(rawList: LoggerHealthVM[]): LoggerHealthVM[] {
    const mergedMap = new Map<string, LoggerHealthVM>();

    rawList.forEach(item => {
      const key = item.trvID ? `TRV-${item.trvID}` : `${item.siteID || 0}-${item.loggerName || ''}`;

      if (!mergedMap.has(key)) {
        mergedMap.set(key, { ...item });
      } else {
        const existing = mergedMap.get(key)!;

        const itemDate = item.updatedDate ? new Date(item.updatedDate) : new Date(0);
        const existingDate = existing.updatedDate ? new Date(existing.updatedDate) : new Date(0);

        if (itemDate > existingDate) {
          const savedMetadata = {
            gatewayKey: existing.gatewayKey || item.gatewayKey,
            deviceString: existing.deviceString || item.deviceString,
            meterName: existing.meterName || item.meterName,
            latestRawReadingDate: this.newestDate(existing.latestRawReadingDate, item.latestRawReadingDate),
            latestAggregatedReadingDate: this.newestDate(existing.latestAggregatedReadingDate, item.latestAggregatedReadingDate),
            latestFileReceivedDate: this.newestDate(existing.latestFileReceivedDate, item.latestFileReceivedDate),
            lastAlertSentDate: this.newestDate(existing.lastAlertSentDate, item.lastAlertSentDate),
            updatedDate: this.newestDate(existing.updatedDate, item.updatedDate),
            logicalGroupID: item.logicalGroupID ?? existing.logicalGroupID,
            configRegister: existing.configRegister || item.configRegister,
            configReadingType: existing.configReadingType || item.configReadingType,
            configValueScale: existing.configValueScale || item.configValueScale,
            configAggregation: existing.configAggregation || item.configAggregation,
            configDescription: existing.configDescription || item.configDescription,
            configUnit: existing.configUnit || item.configUnit,
            sourceDeviceKey: existing.sourceDeviceKey || item.sourceDeviceKey
          };

          Object.assign(existing, item);

          existing.gatewayKey = savedMetadata.gatewayKey;
          existing.deviceString = savedMetadata.deviceString;
          existing.meterName = savedMetadata.meterName;
          existing.latestRawReadingDate = savedMetadata.latestRawReadingDate;
          existing.latestAggregatedReadingDate = savedMetadata.latestAggregatedReadingDate;
          existing.latestFileReceivedDate = savedMetadata.latestFileReceivedDate;
          existing.lastAlertSentDate = savedMetadata.lastAlertSentDate;
          existing.updatedDate = savedMetadata.updatedDate;
          existing.logicalGroupID = savedMetadata.logicalGroupID;
          existing.configRegister = savedMetadata.configRegister;
          existing.configReadingType = savedMetadata.configReadingType;
          existing.configValueScale = savedMetadata.configValueScale;
          existing.configAggregation = savedMetadata.configAggregation;
          existing.configDescription = savedMetadata.configDescription;
          existing.configUnit = savedMetadata.configUnit;
          existing.sourceDeviceKey = savedMetadata.sourceDeviceKey;
        } else {
          existing.gatewayKey = existing.gatewayKey || item.gatewayKey;
          existing.deviceString = existing.deviceString || item.deviceString;
          existing.meterName = existing.meterName || item.meterName;
          existing.latestRawReadingDate = this.newestDate(existing.latestRawReadingDate, item.latestRawReadingDate);
          existing.latestAggregatedReadingDate = this.newestDate(existing.latestAggregatedReadingDate, item.latestAggregatedReadingDate);
          existing.latestFileReceivedDate = this.newestDate(existing.latestFileReceivedDate, item.latestFileReceivedDate);
          existing.lastAlertSentDate = this.newestDate(existing.lastAlertSentDate, item.lastAlertSentDate);
          existing.updatedDate = this.newestDate(existing.updatedDate, item.updatedDate);
          existing.logicalGroupID = existing.logicalGroupID ?? item.logicalGroupID;
          existing.configRegister = existing.configRegister || item.configRegister;
          existing.configReadingType = existing.configReadingType || item.configReadingType;
          existing.configValueScale = existing.configValueScale || item.configValueScale;
          existing.configAggregation = existing.configAggregation || item.configAggregation;
          existing.configDescription = existing.configDescription || item.configDescription;
          existing.configUnit = existing.configUnit || item.configUnit;
          existing.sourceDeviceKey = existing.sourceDeviceKey || item.sourceDeviceKey;
        }
      }
    });

    return Array.from(mergedMap.values());
  }

  // Clean visual sensor type helper (preserves custom/unknown configured categories)
  getCleanType(categoryName: string): string {
    const raw = (categoryName || '').trim();
    if (!raw) return 'General';
    const cat = raw.toLowerCase();
    if (cat.includes('electricity') || cat.includes('voltage') || cat.includes('current') || cat.includes('power')) {
      return 'Electricity';
    }
    if (cat.includes('gas') || cat.includes('chp')) {
      return 'Gas';
    }
    if (cat.includes('water')) {
      return 'Water';
    }
    if (cat.includes('solar')) {
      return 'Solar PV';
    }
    if (cat.includes('heat')) {
      return 'Heat';
    }
    if (cat.includes('oil')) {
      return 'Oil';
    }
    if (cat.includes('biomass')) {
      return 'Biomass';
    }
    if (cat.includes('temperature') || cat.includes('humidity') || cat.includes('sensor')) {
      return 'Sensors';
    }
    return raw;
  }

  // Find corresponding telemetry value inside UserMenuService readings cache
  getLatestReadingForGroup(categoryId: number, groupId: number, resourceName: string): { value: number | null; unit: string; timestamp: Date | null } {
    let match: any = null;
    for (const cat of this.latestValuesData) {
      if (cat.fpcategoryId === categoryId) {
        match = (cat.readings || []).find((r: any) => r.logicalGroupId === groupId || r.name === resourceName);
        if (match) break;
      }
    }

    if (match) {
      return {
        value: match.value,
        unit: match.unit || '',
        timestamp: match.timestamp ? new Date(match.timestamp) : null
      };
    }
    return {
      value: null,
      unit: '',
      timestamp: null
    };
  }

  // Deterministic latest-reading match for a health channel.
  // Primary: LogicalGroupID (never ConfigRegister, which is a DEF_ID).
  // Fallback (only when LogicalGroupID is absent): exact configured name within the channel's own
  // category, and only when that name is unique there. Otherwise the reading stays unavailable.
  private findLatestReadingForChannel(ch: LoggerHealthVM): any | null {
    if (ch.logicalGroupID !== undefined && ch.logicalGroupID !== null) {
      for (const cat of this.latestValuesData) {
        const match = (cat.readings || []).find((r: any) => String(r.logicalGroupId) === String(ch.logicalGroupID));
        if (match) return match;
      }
      return null;
    }

    const name = (ch.configuredName || '').trim();
    if (!name || !ch.fpCategoryId) return null;
    const cat = this.latestValuesData.find((c: any) => c.fpcategoryId === ch.fpCategoryId);
    const candidates = (cat?.readings || []).filter((r: any) => (r.name || '').trim() === name);
    return candidates.length === 1 ? candidates[0] : null;
  }

  // Authoritative physical device key resolution
  getPhysicalMeterKey(ch: LoggerHealthVM): string {
    const vendor = (ch.vendorType || '').trim().toLowerCase();
    const siteId = ch.siteID || this.currentSiteId || 0;

    if (vendor.includes('nd')) {
      // ND Metering requires BOTH DeviceString AND MeterName for authoritative physical grouping
      const device = (ch.deviceString || '').trim();
      const meter = (ch.meterName || '').trim();
      if (device && meter) {
        return `NDMetering|${siteId}|${device}|${meter}`;
      }
      // Do NOT group by DeviceString alone or MeterName alone.
      // If authoritative pair is incomplete, isolate safely.
    } else if (vendor.includes('epi')) {
      // EpiSensor: Use scanner-generated authoritative SourceDeviceKey when formatted as EpiSensor-{NetworkSID}-{ModuleID}
      const srcKey = (ch.sourceDeviceKey || '').trim();
      if (srcKey.startsWith('EpiSensor-')) {
        return `EpiSensor|${siteId}|${srcKey}`;
      }
      const gateway = (ch.gatewayKey || '').trim();
      const module = (ch.meterName || '').trim();
      if (gateway && module) {
        return `EpiSensor|${siteId}|${gateway}|${module}`;
      }
    }

    // Fail-Safe Fallback: Prefer ISOLATION over accidental consolidation.
    // Do NOT merge independent physical meters merely because they share a generic meterName or loggerName.
    // Use unique internal technical identity (SourceDeviceKey or TrvID) to isolate unknown physical devices.
    if (ch.sourceDeviceKey && ch.sourceDeviceKey.trim() !== '') {
      return `Source|${siteId}|${ch.sourceDeviceKey.trim()}`;
    }
    return `TRV|${siteId}|${ch.trvID || ch.healthStatusID || 'UNRESOLVED'}`;
  }

  // Group physical registers into Customer Physical Meters
  getCustomerMeters(channels: LoggerHealthVM[]): CustomerMeterRow[] {
    const result: CustomerMeterRow[] = [];

    // Map of active direct LogicalGroups from liveDataConfig
    const directLogicalGroupsMap = new Map<number, { catId: number; catName: string; groupName: string; monitoringSourceType: number }>();
    const staticLogicalGroups: Array<{ catId: number; catName: string; groupName: string; groupId: number }> = [];

    if (this.liveDataConfig && this.liveDataConfig.length > 0) {
      this.liveDataConfig.forEach(cat => {
        const catName = cat.categoryName || 'Other Meters';
        const catId = cat.fpcategoryId || 0;

        cat.groups?.forEach((group: ReviewableGroupVM) => {
          const groupName = group.name || 'Unknown Meter';
          const groupId = group.id || 0;
          const msType = group.monitoringSourceType || 0;

          // Structural filter: skip calculated/policy resources
          if (group.isCalculatedMeasurement || (group.type && group.type.toLowerCase().includes('policy'))) {
            return;
          }

          if (msType === 2) {
            staticLogicalGroups.push({ catId, catName, groupName, groupId });
          } else if (groupId > 0) {
            directLogicalGroupsMap.set(groupId, { catId, catName, groupName, monitoringSourceType: msType });
          }
        });
      });
    }

    // -------------------------------------------------------------------------------------
    // A. BUILD LIVE PHYSICAL METERS
    // -------------------------------------------------------------------------------------
    // Filter active live channels (direct logical group mappings, not static)
    const liveChannels = channels.filter(ch => ch.isShownInCustomerView && ch.monitoringSourceType !== 2);

    // Group live channels by Authoritative Physical Device Key
    const physicalGroupMap = new Map<string, LoggerHealthVM[]>();

    liveChannels.forEach(ch => {
      const key = this.getPhysicalMeterKey(ch);
      if (!physicalGroupMap.has(key)) {
        physicalGroupMap.set(key, []);
      }
      physicalGroupMap.get(key)!.push(ch);
    });

    physicalGroupMap.forEach((meterChannels, physicalKey) => {
      if (meterChannels.length === 0) return;

      // 1. Resolve Primary Channel for Name & Resource Type
      let primaryCh = meterChannels.find(ch => {
        const rt = (ch.configReadingType || '').toUpperCase();
        const desc = (ch.configDescription || ch.configuredName || '').toLowerCase();
        return rt.includes('KWH') || rt === 'E' || desc.includes('energy') || desc.includes('total') || desc.includes('incomer') || desc.includes('main');
      });

      if (!primaryCh) {
        primaryCh = meterChannels.find(ch => ch.configuredName && ch.configuredName.trim() !== '') || meterChannels[0];
      }

      // 2. Resolve Friendly Meter Name
      let meterName = '';
      if (primaryCh.configuredName && primaryCh.configuredName.trim() !== '') {
        meterName = primaryCh.configuredName.trim();
      } else if (primaryCh.meterName && primaryCh.meterName.trim() !== '') {
        meterName = primaryCh.meterName.trim();
      } else if (primaryCh.loggerName && primaryCh.loggerName.trim() !== '') {
        meterName = primaryCh.loggerName.trim();
      } else {
        meterName = primaryCh.fpCategoryName ? `${this.getCleanType(primaryCh.fpCategoryName)} Meter` : 'Energy Meter';
      }

      // 3. Resolve Resource Type
      const catName = primaryCh.fpCategoryName || 'Other Meters';
      const resourceType = this.getCleanType(catName);

      // 4. Resolve Health Status (Worst monitored channel status)
      const statusPriority = [
        'offline',
        'import error',
        'processing stuck',
        'aggregation delayed',
        'aggregation missing',
        'unmapped',
        'frozen value',
        'late',
        'healthy'
      ];

      let worstRank = statusPriority.length;
      let worstState = 'healthy';
      let monitoredCount = 0;

      // Unrecognised non-empty statuses rank just above 'healthy' so they can never read as Healthy,
      // while any known issue status still takes precedence.
      const unknownRank = statusPriority.indexOf('healthy') - 0.5;

      meterChannels.forEach(ch => {
        const state = (ch.currentStatus || '').trim().toLowerCase();
        if (state) {
          monitoredCount++;
          const knownRank = statusPriority.indexOf(state);
          const rank = knownRank !== -1 ? knownRank : unknownRank;
          if (rank < worstRank) {
            worstRank = rank;
            worstState = knownRank !== -1 ? state : 'unknown';
          }
        }
      });

      let status: 'Healthy' | 'Needs Attention' = 'Healthy';
      let message = 'Data is arriving normally.';

      if (worstState === 'healthy' || worstRank === statusPriority.indexOf('healthy')) {
        status = 'Healthy';
        message = 'Data is arriving normally.';
      } else {
        status = 'Needs Attention';
        if (worstState === 'offline') {
          message = 'We have not received data from this meter recently.';
        } else if (worstState === 'late') {
          message = 'Data is arriving later than expected.';
        } else if (worstState === 'aggregation delayed') {
          message = 'Meter data has arrived, but reporting is still processing.';
        } else if (worstState === 'aggregation missing') {
          message = 'Data is arriving, but summary totals are missing.';
        } else if (worstState === 'import error' || worstState === 'processing stuck') {
          message = 'We are having difficulty processing this meter\'s data.';
        } else if (worstState === 'frozen value') {
          message = 'Data values have remained unchanged.';
        } else if (worstState === 'unmapped') {
          message = 'Device configuration review is required.';
        } else {
          message = 'Meter requires attention.';
        }
      }

      // 5. Calculate Latest Reading & Timestamp
      let lastReading: Date | null = null;
      let lastUpdated: Date | null = null;

      meterChannels.forEach(ch => {
        const raw = ch.latestRawReadingDate ? new Date(ch.latestRawReadingDate) : (ch.latestAggregatedReadingDate ? new Date(ch.latestAggregatedReadingDate) : null);
        if (raw && (!lastReading || raw > lastReading)) {
          lastReading = raw;
        }
        const upd = ch.updatedDate ? new Date(ch.updatedDate) : null;
        if (upd && (!lastUpdated || upd > lastUpdated)) {
          lastUpdated = upd;
        }
      });

      // 6. Build Underlying Measurements for Expandable Drawer
      const underlyingMeasurements: UnderlyingMeasurementVM[] = meterChannels.map(ch => {
        const name = ch.configDescription || ch.configuredName || ch.meterName || (ch.configReadingType ? `${ch.configReadingType} Measurement` : 'Metering Channel');
        let latestVal: number | null = null;
        let unit = ch.configUnit || '';

        // Latest group reading lookup (GetLatestReadings / LastHourlyData).
        // Values are already scaled by fn_IPON_ApplyScale during import, so no ValueScale is applied here.
        const match = this.findLatestReadingForChannel(ch);
        if (match && match.value !== null && match.value !== undefined) {
          latestVal = match.value;
          if (match.unit) unit = match.unit;
        }

        const chRaw = ch.latestRawReadingDate ? new Date(ch.latestRawReadingDate) : (ch.latestAggregatedReadingDate ? new Date(ch.latestAggregatedReadingDate) : null);

        return {
          name: name,
          readingType: ch.configReadingType,
          latestValue: latestVal,
          unit: unit,
          status: ch.currentStatus || 'Unmonitored',
          lastReading: chRaw,
          trvId: ch.trvID || undefined,
          register: ch.configRegister || undefined,
          scale: ch.configValueScale || undefined,
          isMonitored: (ch.healthStatusID || 0) > 0
        };
      });

      // Primary reading value for the top-level card
      const primaryMeasurement = underlyingMeasurements.find(m => m.latestValue !== null) || underlyingMeasurements[0];

      result.push({
        siteName: primaryCh.siteName || this.currentSiteName,
        categoryName: catName,
        fpCategoryName: catName,
        meterName: meterName,
        configuredName: primaryCh.configuredName,
        loggerName: primaryCh.loggerName,
        type: resourceType,
        latestValue: primaryMeasurement?.latestValue ?? null,
        unit: primaryMeasurement?.unit || '',
        status: status,
        monitoringSourceType: 1,
        lastReading: lastReading,
        lastUpdated: lastUpdated || lastReading,
        message: message,
        detailsExpanded: false,
        supportDetailsExpanded: false,
        channels: meterChannels,
        underlyingMeasurements: underlyingMeasurements,
        physicalDeviceKey: physicalKey,
        monitoredChannelCount: monitoredCount,
        totalChannelCount: meterChannels.length,
        isStaticSection: false,
        coverageDiagnostic: monitoredCount === meterChannels.length
          ? 'Full Logger Health Coverage'
          : (monitoredCount > 0 ? 'Partial Logger Health Coverage' : 'No Logger Health Coverage'),
        trvID: primaryCh.trvID,
        configRegister: primaryCh.configRegister,
        configReadingType: primaryCh.configReadingType,
        configValueScale: primaryCh.configValueScale,
        configAggregation: primaryCh.configAggregation,
        configDescription: primaryCh.configDescription,
        configUnit: primaryCh.configUnit,
        sourceDeviceKey: primaryCh.sourceDeviceKey,
        meterNameConfig: primaryCh.meterName,
        gatewayKey: primaryCh.gatewayKey,
        deviceString: primaryCh.deviceString,
        vendorType: primaryCh.vendorType,
        currentStatus: primaryCh.currentStatus,
        suspectedFailureStage: primaryCh.suspectedFailureStage
      });
    });

    // -------------------------------------------------------------------------------------
    // B. BUILD STATIC / BILL-BASED MEASUREMENTS
    // -------------------------------------------------------------------------------------
    staticLogicalGroups.forEach(st => {
      const latestInfo = this.getLatestReadingForGroup(st.catId, st.groupId, st.groupName);
      result.push({
        siteName: this.currentSiteName,
        categoryName: st.catName,
        fpCategoryName: st.catName,
        meterName: st.groupName,
        configuredName: st.groupName,
        type: this.getCleanType(st.catName),
        latestValue: latestInfo.value,
        unit: latestInfo.unit,
        status: 'Static Data',
        monitoringSourceType: 2,
        isStaticSection: true,
        lastReading: latestInfo.timestamp,
        lastUpdated: latestInfo.timestamp,
        message: 'This measurement is updated manually or from bills. Live monitoring is not expected.',
        detailsExpanded: false,
        channels: [],
        underlyingMeasurements: []
      });
    });

    const directStaticChannels = channels.filter(ch => ch.isShownInCustomerView && ch.monitoringSourceType === 2);
    directStaticChannels.forEach(ch => {
      const name = ch.configuredName || ch.configDescription || ch.meterName || 'Static Measurement';
      const exists = result.some(r => r.isStaticSection && r.meterName === name);
      if (!exists) {
        result.push({
          siteName: ch.siteName || this.currentSiteName,
          categoryName: ch.fpCategoryName || 'General',
          fpCategoryName: ch.fpCategoryName || 'General',
          meterName: name,
          configuredName: ch.configuredName,
          type: this.getCleanType(ch.fpCategoryName || 'General'),
          latestValue: null,
          unit: ch.configUnit || '',
          status: 'Static Data',
          monitoringSourceType: 2,
          isStaticSection: true,
          lastReading: ch.latestRawReadingDate ? new Date(ch.latestRawReadingDate) : null,
          lastUpdated: ch.updatedDate ? new Date(ch.updatedDate) : null,
          message: 'This measurement is updated manually or from bills. Live monitoring is not expected.',
          detailsExpanded: false,
          channels: [ch],
          underlyingMeasurements: []
        });
      }
    });

    return result;
  }

  // Banner status properties based on UNFILTERED site live meters
  get bannerType(): 'healthy' | 'warning' | 'info' {
    const liveMeters = this.allSiteLiveMeters || [];
    if (liveMeters.length === 0) return 'info';
    const needsAttention = liveMeters.filter(m => m.status === 'Needs Attention' || m.status === 'Delayed' || m.status === 'Offline');
    return needsAttention.length === 0 ? 'healthy' : 'warning';
  }

  get bannerTitle(): string {
    const liveMeters = this.allSiteLiveMeters || [];
    if (liveMeters.length === 0) {
      return 'Live monitoring information is currently unavailable.';
    }
    const needsAttention = liveMeters.filter(m => m.status === 'Needs Attention' || m.status === 'Delayed' || m.status === 'Offline');
    if (needsAttention.length === 0) {
      return 'All monitored meters are working normally.';
    }
    return needsAttention.length === 1 ? '1 meter needs attention.' : `${needsAttention.length} meters need attention.`;
  }

  get bannerSubtitle(): string {
    const liveMeters = this.allSiteLiveMeters || [];
    if (liveMeters.length === 0) {
      return 'No live physical meters are currently reporting for this site.';
    }
    const needsAttention = liveMeters.filter(m => m.status === 'Needs Attention' || m.status === 'Delayed' || m.status === 'Offline');
    if (needsAttention.length === 0) {
      return 'Data is arriving and processing as expected.';
    }
    return 'One or more meters have communication or processing delays.';
  }

  get isStagingSnapshotStale(): boolean {
    if (!this.isSuperAdmin) return false;
    if (!this.allLoggers || this.allLoggers.length === 0) return false;
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const hasRecent = this.allLoggers.some(l => {
      const d = l.updatedDate ? new Date(l.updatedDate) : (l.latestRawReadingDate ? new Date(l.latestRawReadingDate) : null);
      return d && d > thirtyDaysAgo;
    });
    return !hasRecent;
  }

  get liveMetersList(): CustomerMeterRow[] {
    return this.filteredLoggers.filter(m => !m.isStaticSection);
  }

  get needsAttentionMeters(): CustomerMeterRow[] {
    return this.filteredLoggers.filter(m => !m.isStaticSection && (m.status === 'Needs Attention' || m.status === 'Delayed' || m.status === 'Offline'));
  }

  get healthyMeters(): CustomerMeterRow[] {
    return this.filteredLoggers.filter(m => !m.isStaticSection && (m.status === 'Healthy' || m.status === 'Online'));
  }

  get staticMetersList(): CustomerMeterRow[] {
    return this.filteredLoggers.filter(m => m.isStaticSection);
  }

  toggleNeedsAttentionSection(): void {
    this.needsAttentionSectionExpanded = !this.needsAttentionSectionExpanded;
    this.cdr.markForCheck();
  }

  toggleHealthySection(): void {
    this.healthySectionManuallyToggled = true;
    this.healthySectionExpanded = !this.healthySectionExpanded;
    this.cdr.markForCheck();
  }

  toggleStaticSection(): void {
    this.staticSectionExpanded = !this.staticSectionExpanded;
    this.cdr.markForCheck();
  }


  setFilterChip(chip: 'ALL' | 'NEEDS_ATTENTION' | 'HEALTHY' | 'STATIC'): void {
    this.activeFilterChip = chip;
    if (chip === 'ALL') {
      this.activeKpiFilter = 'NONE';
      this.statusFilter = 'ALL';
    } else if (chip === 'NEEDS_ATTENTION') {
      this.activeKpiFilter = 'WARNING';
      this.statusFilter = 'NEEDS_ATTENTION';
    } else if (chip === 'HEALTHY') {
      this.activeKpiFilter = 'HEALTHY';
      this.statusFilter = 'HEALTHY';
    } else if (chip === 'STATIC') {
      this.activeKpiFilter = 'STATIC';
      this.statusFilter = 'STATIC';
    }
    this.applyFilters();
  }

  getFreshnessPill(meter: any): { label: string; class: string } {
    if (meter.status === 'Static Data' || meter.monitoringSourceType === 2 || meter.isStaticSection) {
      return { label: 'Static', class: 'freshness-static' };
    }
    const timeVal = meter.lastReading || this.getLatestReadingTime(meter);
    if (!timeVal) {
      return { label: 'No Data', class: 'freshness-old' };
    }
    try {
      const readingDate = new Date(timeVal);
      if (isNaN(readingDate.getTime())) {
        return { label: 'No Data', class: 'freshness-old' };
      }
      const now = new Date();
      const diffMs = now.getTime() - readingDate.getTime();
      const diffHours = diffMs / (1000 * 60 * 60);

      if (diffHours <= 2) {
        return { label: 'Fresh', class: 'freshness-fresh' };
      } else if (diffHours <= 24) {
        return { label: 'Delayed', class: 'freshness-delayed' };
      } else {
        return { label: 'Old', class: 'freshness-old' };
      }
    } catch {
      return { label: 'No Data', class: 'freshness-old' };
    }
  }

  getResourceTypeClass(type?: string): string {
    const t = (type || '').toLowerCase();
    if (t.includes('elec') || t.includes('power') || t.includes('volt') || t.includes('curr')) return 'res-electricity';
    if (t.includes('gas')) return 'res-gas';
    if (t.includes('water')) return 'res-water';
    if (t.includes('solar')) return 'res-solar';
    if (t.includes('heat') || t.includes('chp') || t.includes('steam')) return 'res-heat';
    if (t.includes('oil')) return 'res-oil';
    if (t.includes('bio')) return 'res-biomass';
    if (t.includes('temp') || t.includes('humid') || t.includes('sens')) return 'res-sensors';
    return 'res-general';
  }

  getResourceInitial(type?: string): string {
    const raw = (type || '').trim();
    if (!raw) return 'M';
    const t = raw.toLowerCase();
    if (t.includes('elec') || t.includes('power') || t.includes('volt') || t.includes('curr')) return 'E';
    if (t.includes('gas') || t.includes('chp')) return 'G';
    if (t.includes('water')) return 'W';
    if (t.includes('solar') || t.includes('pv')) return 'S';
    if (t.includes('heat') || t.includes('steam')) return 'H';
    if (t.includes('oil')) return 'O';
    if (t.includes('bio')) return 'B';
    if (t.includes('temp') || t.includes('humid') || t.includes('sens')) return 'T';
    if (t.includes('waste')) return 'W';
    if (t.includes('transport') || t.includes('fleet')) return 'TR';

    const words = raw.split(/\s+/).filter(w => w.length > 0);
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return raw.substring(0, Math.min(2, raw.length)).toUpperCase();
  }

  getVendorInitials(vendor?: string | null): string {
    const raw = (vendor || '').trim();
    if (!raw) return 'UN';
    const v = raw.toLowerCase();
    if (v.includes('nd')) return 'ND';
    if (v.includes('epi')) return 'ES';
    if (v.includes('schneider') || v.includes('pm')) return 'PM';
    if (v.includes('elster') || v.includes('honeywell')) return 'HW';
    if (v.includes('abb')) return 'ABB';
    const parts = raw.split(/\s+/).filter(p => p.length > 0);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return raw.substring(0, Math.min(2, raw.length)).toUpperCase();
  }


  onHealthyHeaderKeydown(event: KeyboardEvent): void {
    if (this.warningCount > 0 && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      this.toggleHealthySection();
    }
  }

  onStatusFilterChange(): void {
    if (this.showCustomerView) {
      if (this.statusFilter === 'HEALTHY') {
        this.activeKpiFilter = 'HEALTHY';
        this.activeFilterChip = 'HEALTHY';
      } else if (this.statusFilter === 'NEEDS_ATTENTION') {
        this.activeKpiFilter = 'WARNING';
        this.activeFilterChip = 'NEEDS_ATTENTION';
      } else if (this.statusFilter === 'STATIC') {
        this.activeKpiFilter = 'STATIC';
        this.activeFilterChip = 'STATIC';
      } else {
        this.activeKpiFilter = 'NONE';
        this.activeFilterChip = 'ALL';
      }
    } else {
      if (this.statusFilter === 'HEALTHY') this.activeKpiFilter = 'HEALTHY';
      else this.activeKpiFilter = 'NONE';
    }
    this.applyFilters();
  }

  // Handle live UI search/filtering logic
  applyFilters(): void {
    let list = [...this.allLoggers];

    if (this.showCustomerView) {
      // 1. Group physical meters for the unfiltered site collection
      let allCustomerMeters = this.getCustomerMeters(list);

      const liveMeters = allCustomerMeters.filter(m => !m.isStaticSection);
      const staticMeters = allCustomerMeters.filter(m => m.isStaticSection);

      this.allSiteLiveMeters = liveMeters;

      // 2. Summary KPI counts: Live Meters = Healthy + Needs Attention
      this.totalCount = liveMeters.length;
      this.healthyCount = liveMeters.filter(m => m.status === 'Healthy' || m.status === 'Online').length;
      this.warningCount = liveMeters.filter(m => m.status === 'Needs Attention' || m.status === 'Delayed' || m.status === 'Offline').length;
      this.pipelineCount = 0;
      this.staticCount = staticMeters.length;

      if (!this.healthySectionManuallyToggled) {
        this.healthySectionExpanded = true;
      }


      // 3. Apply Text Search
      let filtered = allCustomerMeters;
      if (this.searchTerm && this.searchTerm.trim() !== '') {
        const search = this.searchTerm.toLowerCase();
        filtered = filtered.filter(m =>
          (m.meterName && m.meterName.toLowerCase().includes(search)) ||
          (m.type && m.type.toLowerCase().includes(search)) ||
          (m.message && m.message.toLowerCase().includes(search)) ||
          (m.status && m.status.toLowerCase().includes(search))
        );
      }

      // 4. Apply KPI or Status dropdown filter
      if (this.activeKpiFilter !== 'NONE') {
        if (this.activeKpiFilter === 'TOTAL') {
          filtered = filtered.filter(m => !m.isStaticSection);
        } else if (this.activeKpiFilter === 'HEALTHY') {
          filtered = filtered.filter(m => !m.isStaticSection && (m.status === 'Healthy' || m.status === 'Online'));
        } else if (this.activeKpiFilter === 'WARNING') {
          filtered = filtered.filter(m => !m.isStaticSection && (m.status === 'Needs Attention' || m.status === 'Delayed' || m.status === 'Offline'));
        } else if (this.activeKpiFilter === 'STATIC') {
          filtered = filtered.filter(m => m.isStaticSection);
        }
      } else if (this.statusFilter !== 'ALL') {
        if (this.statusFilter === 'HEALTHY') {
          filtered = filtered.filter(m => !m.isStaticSection && (m.status === 'Healthy' || m.status === 'Online'));
        } else if (this.statusFilter === 'NEEDS_ATTENTION' || this.statusFilter === 'WARNING') {
          filtered = filtered.filter(m => !m.isStaticSection && (m.status === 'Needs Attention' || m.status === 'Delayed' || m.status === 'Offline'));
        } else if (this.statusFilter === 'STATIC') {
          filtered = filtered.filter(m => m.isStaticSection);
        }
      }

      this.filteredLoggers = filtered;
    } else {
      // Global Support Engineering View filtering
      if (this.searchTerm && this.searchTerm.trim() !== '') {
        const search = this.searchTerm.toLowerCase();
        list = list.filter(l =>
          (l.siteName && l.siteName.toLowerCase().includes(search)) ||
          (l.loggerName && l.loggerName.toLowerCase().includes(search)) ||
          (l.meterName && l.meterName.toLowerCase().includes(search)) ||
          (l.sourceDeviceKey && l.sourceDeviceKey.toLowerCase().includes(search)) ||
          (l.gatewayKey && l.gatewayKey.toLowerCase().includes(search)) ||
          (l.vendorType && l.vendorType.toLowerCase().includes(search)) ||
          (l.configRegister && l.configRegister.toLowerCase().includes(search)) ||
          (l.currentStatus && l.currentStatus.toLowerCase().includes(search)) ||
          (l.suspectedFailureStage && l.suspectedFailureStage.toLowerCase().includes(search)) ||
          (l.trvID && l.trvID.toString().includes(search))
        );
      }

      if (this.vendorFilter !== 'ALL') {
        list = list.filter(item => item.vendorType === this.vendorFilter);
      }

      if (this.activityFilter !== 'ALL') {
        const months = parseInt(this.activityFilter, 10);
        if (!isNaN(months) && months > 0) {
          const cutoffDate = new Date();
          cutoffDate.setMonth(cutoffDate.getMonth() - months);

          list = list.filter(item => {
            const fileDate = item.latestFileReceivedDate ? new Date(item.latestFileReceivedDate) : null;
            const rawDate = item.latestRawReadingDate ? new Date(item.latestRawReadingDate) : null;
            const aggDate = item.latestAggregatedReadingDate ? new Date(item.latestAggregatedReadingDate) : null;

            const dates = [fileDate, rawDate, aggDate].filter(d => d !== null) as Date[];
            if (dates.length === 0) return false;
            const maxDate = new Date(Math.max(...dates.map(d => d.getTime())));
            return maxDate >= cutoffDate;
          });
        }
      }

      this.calculateSummaryCounts(list);

      if (this.activeKpiFilter !== 'NONE') {
        if (this.activeKpiFilter === 'HEALTHY') {
          list = list.filter(item => this.getSupportStatusCategory(item.currentStatus) === 'healthy');
        } else if (this.activeKpiFilter === 'WARNING') {
          list = list.filter(item => this.getSupportStatusCategory(item.currentStatus) === 'warning');
        } else if (this.activeKpiFilter === 'PIPELINE') {
          list = list.filter(item => this.getSupportStatusCategory(item.currentStatus) === 'pipeline');
        }
      } else if (this.statusFilter !== 'ALL') {
        list = list.filter(item => {
          if (!item.currentStatus) return false;
          const status = item.currentStatus.toUpperCase();
          if (this.statusFilter === 'STUCK') return status === 'PROCESSING STUCK';
          if (this.statusFilter === 'IMPORTERROR') return status === 'IMPORT ERROR';
          if (this.statusFilter === 'AGGDELAYED') return status === 'AGGREGATION DELAYED';
          if (this.statusFilter === 'AGGMISSING') return status === 'AGGREGATION MISSING';
          if (this.statusFilter === 'FROZEN') return status === 'FROZEN VALUE';
          if (this.statusFilter === 'HEALTHY') return status === 'HEALTHY';
          if (this.statusFilter === 'LATE') return status === 'LATE';
          if (this.statusFilter === 'OFFLINE') return status === 'OFFLINE';
          if (this.statusFilter === 'UNMAPPED') return status === 'UNMAPPED';
          return status === this.statusFilter.toUpperCase();
        });
      }

      if (this.sortColumn) {
        list.sort((a, b) => {
          let valA: any;
          let valB: any;
          if (this.sortColumn === 'lastReading') {
            valA = a.latestRawReadingDate ? new Date(a.latestRawReadingDate).getTime() : 0;
            valB = b.latestRawReadingDate ? new Date(b.latestRawReadingDate).getTime() : 0;
          } else if (this.sortColumn === 'updatedDate') {
            valA = a.updatedDate ? new Date(a.updatedDate).getTime() : 0;
            valB = b.updatedDate ? new Date(b.updatedDate).getTime() : 0;
          } else {
            valA = (a as any)[this.sortColumn] || '';
            valB = (b as any)[this.sortColumn] || '';
            if (typeof valA === 'string') valA = valA.toLowerCase();
            if (typeof valB === 'string') valB = valB.toLowerCase();
          }
          if (valA < valB) return this.sortDirection === 'asc' ? -1 : 1;
          if (valA > valB) return this.sortDirection === 'asc' ? 1 : -1;
          return 0;
        });
      }

      this.filteredLoggers = list.map(l => ({
        ...l,
        siteName: l.siteName || undefined,
        categoryName: l.fpCategoryName || undefined,
        fpCategoryName: l.fpCategoryName || undefined,
        meterName: l.configuredName || l.meterName || l.loggerName || undefined,
        status: (l.currentStatus as any) || 'Status Unavailable',
        monitoringSourceType: l.monitoringSourceType || 0,
        lastReading: l.latestRawReadingDate ? new Date(l.latestRawReadingDate) : (l.latestAggregatedReadingDate ? new Date(l.latestAggregatedReadingDate) : null),
        lastUpdated: l.updatedDate ? new Date(l.updatedDate) : null,
        message: l.suspectedFailureStage || 'Technical channel details',
        channels: [l]
      }));

      if (this.selectedSupportLogger) {
        const found = this.filteredLoggers.find(l => l.healthStatusID === this.selectedSupportLogger?.healthStatusID || (l.sourceDeviceKey && l.sourceDeviceKey === this.selectedSupportLogger?.sourceDeviceKey));
        this.selectedSupportLogger = found || (this.filteredLoggers.length > 0 ? this.filteredLoggers[0] : null);
      } else if (this.filteredLoggers.length > 0) {
        this.selectedSupportLogger = this.filteredLoggers[0];
      }

    }
  }

  get hasActiveFilters(): boolean {
    return (this.searchTerm && this.searchTerm.trim() !== '') ||
           this.vendorFilter !== 'ALL' ||
           this.statusFilter !== 'ALL' ||
           this.activeKpiFilter !== 'NONE' ||
           this.activeFilterChip !== 'ALL' ||
           (this.activeContributorFilterIds !== null && this.activeContributorFilterIds.length > 0);
  }

  selectKpiFilter(filter: string): void {
    if (this.activeKpiFilter === filter) {
      this.activeKpiFilter = 'NONE';
      this.activeFilterChip = 'ALL';
    } else {
      this.activeKpiFilter = filter;
      if (filter === 'HEALTHY') this.activeFilterChip = 'HEALTHY';
      else if (filter === 'WARNING') this.activeFilterChip = 'NEEDS_ATTENTION';
      else if (filter === 'STATIC') this.activeFilterChip = 'STATIC';
      else if (filter === 'TOTAL') this.activeFilterChip = 'ALL';
    }
    this.statusFilter = 'ALL';
    this.applyFilters();
  }

  resetFilters(): void {
    this.searchTerm = '';
    this.vendorFilter = 'ALL';
    this.statusFilter = 'ALL';
    this.activeKpiFilter = 'NONE';
    this.activeFilterChip = 'ALL';
    this.activityFilter = 'ALL';
    this.activeContributorFilterIds = null;
    this.activeContributorParentName = '';
    this.applyFilters();
  }

  exportToCsv(): void {
    if (this.filteredLoggers.length === 0) return;

    let headers: string[] = [];
    let rows: any[] = [];

    const formatDate = (dateVal: any): string => {
      if (!dateVal) return '-';
      try {
        const d = new Date(dateVal);
        if (isNaN(d.getTime())) return '-';
        const pad = (n: number) => n.toString().padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
      } catch (e) {
        return '-';
      }
    };

    const escapeCsvValue = (val: any) => {
      const strVal = val === null || val === undefined ? '' : String(val);
      let formatted = strVal.replace(/"/g, '""');
      if (formatted.includes(',') || formatted.includes('\n') || formatted.includes('"')) {
        formatted = `"${formatted}"`;
      }
      return formatted;
    };

    if (this.showCustomerView) {
      headers = [
        'Meter Name',
        'Resource Type',
        'Health Status',
        'Explanation',
        'Monitored Registers',
        'Last Reading',
        'Data Age'
      ];
      rows = this.filteredLoggers.map(m => [
        this.getMeterName(m),
        this.getMeterType(m),
        m.status || '-',
        m.message || '-',
        m.isStaticSection ? 'Static Data' : `${m.monitoredChannelCount || 0} / ${m.totalChannelCount || 0}`,
        m.isStaticSection ? '-' : formatDate(m.lastReading),
        m.isStaticSection ? 'Not applicable' : this.getDataAge(m)
      ]);
    } else {
      headers = [
        'Site Name',
        'Friendly Description',
        'Full Meter / Device Identifier',
        'Register / Line',
        'TRV ID',
        'Reading Type',
        'Aggregation',
        'Vendor',
        'Last Raw DB',
        'Last Agg DB',
        'Status',
        'Failure Stage',
        'Updated Date'
      ];
      rows = this.filteredLoggers.map(l => [
        l.siteName || '-',
        this.getMeterName(l) === 'Meter description unavailable' ? '-' : this.getMeterName(l),
        l.sourceDeviceKey || '-',
        l.configRegister || '-',
        l.trvID || '-',
        l.configReadingType || '-',
        l.configAggregation || '-',
        l.vendorType || '-',
        formatDate(l.latestRawReadingDate),
        formatDate(l.latestAggregatedReadingDate),
        l.currentStatus || '-',
        l.suspectedFailureStage || '-',
        formatDate(l.updatedDate)
      ]);
    }

    const csvContent = [
      headers.map(escapeCsvValue).join(','),
      ...rows.map(row => row.map(escapeCsvValue).join(','))
    ].join('\n');

    const now = new Date();
    const pad = (n: number) => n.toString().padStart(2, '0');
    const yyyy = now.getFullYear();
    const mm = pad(now.getMonth() + 1);
    const dd = pad(now.getDate());
    const hh = pad(now.getHours());
    const min = pad(now.getMinutes());
    const filename = `logger-health-dashboard-${yyyy}${mm}${dd}-${hh}${min}.csv`;

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  getSupportStatusCategory(status: string | undefined | null): 'healthy' | 'warning' | 'pipeline' | 'unknown' {
    if (!status) return 'unknown';
    const s = status.trim().toLowerCase();
    if (s === 'healthy') {
      return 'healthy';
    }
    if (s === 'late' || s === 'offline') {
      return 'warning';
    }
    if (s === 'processing stuck' || s === 'import error' || s === 'aggregation delayed' || s === 'aggregation missing' || s === 'unmapped' || s === 'frozen value') {
      return 'pipeline';
    }
    return 'unknown';
  }

  private calculateSummaryCounts(baseList: LoggerHealthVM[]): void {
    this.totalCount = baseList.length;
    this.healthyCount = baseList.filter(l => this.getSupportStatusCategory(l.currentStatus) === 'healthy').length;
    this.warningCount = baseList.filter(l => this.getSupportStatusCategory(l.currentStatus) === 'warning').length;
    this.pipelineCount = baseList.filter(l => this.getSupportStatusCategory(l.currentStatus) === 'pipeline').length;
  }

  // Global Support KPI rings: share of Total Loggers in the current view (real proportion, not a trend)
  getSupportShare(count: number): number {
    if (this.totalCount <= 0 || !count || count <= 0) return 0;
    return Math.min(100, Math.max(0, (count / this.totalCount) * 100));
  }

  formatSupportShare(count: number): string {
    if (this.totalCount <= 0 || !count || count <= 0) return '0%';
    const pct = this.getSupportShare(count);
    if (pct > 0 && pct < 1) return `${pct.toFixed(1)}%`;
    if (pct > 99 && pct < 100) return '99%';
    return `${Math.round(pct)}%`;
  }

  // Search, vendor and activity window narrow Total Loggers; KPI/status filters do not
  get isSupportScopeFiltered(): boolean {
    return (!!this.searchTerm && this.searchTerm.trim() !== '') ||
           this.vendorFilter !== 'ALL' ||
           this.activityFilter !== 'ALL';
  }

  // -------------------------------------------------------------------------------------
  // Maintenance View Analytical KPI Visualisation Helpers
  // -------------------------------------------------------------------------------------
  getMaintenanceResourceBreakdown(): { name: string; count: number; pct: number; offset: number; color: string }[] {
    const allMeters = this.getCustomerMeters(this.allLoggers);
    if (!allMeters || allMeters.length === 0) return [];

    const total = allMeters.length;
    const catMap = new Map<string, number>();

    allMeters.forEach(m => {
      const type = (m.type || m.categoryName || 'General').trim();
      const norm = type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
      catMap.set(norm, (catMap.get(norm) || 0) + 1);
    });

    const sorted = Array.from(catMap.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);

    const colorForType = (t: string): string => {
      const low = t.toLowerCase();
      if (low.includes('elec') || low.includes('power')) return '#3b82f6'; // blue
      if (low.includes('gas')) return '#f59e0b'; // amber
      if (low.includes('water')) return '#8b5cf6'; // purple
      if (low.includes('solar')) return '#06b6d4'; // cyan
      if (low.includes('heat') || low.includes('chp') || low.includes('steam')) return '#ef4444'; // red
      if (low.includes('oil') || low.includes('fuel')) return '#78716c'; // stone
      return '#64748b'; // slate
    };

    let items: { name: string; count: number; color: string }[] = [];
    if (sorted.length <= 3) {
      items = sorted.map(s => ({ name: s.name, count: s.count, color: colorForType(s.name) }));
    } else {
      const top3 = sorted.slice(0, 3);
      const otherCount = sorted.slice(3).reduce((acc, curr) => acc + curr.count, 0);
      items = top3.map(s => ({ name: s.name, count: s.count, color: colorForType(s.name) }));
      if (otherCount > 0) {
        items.push({ name: 'Other', count: otherCount, color: '#94a3b8' });
      }
    }

    let runningOffset = 0;
    return items.map(item => {
      const pct = total > 0 ? (item.count / total) * 100 : 0;
      const res = {
        name: item.name,
        count: item.count,
        pct: pct,
        offset: runningOffset,
        color: item.color
      };
      runningOffset += pct;
      return res;
    });
  }

  getMaintenanceNeedsAttentionBreakdown(): { label: string; count: number; pctOfMax: number; color: string }[] {
    const liveMeters = this.allSiteLiveMeters || [];
    const warningMeters = liveMeters.filter(m => m.status === 'Needs Attention' || m.status === 'Delayed' || m.status === 'Offline');
    if (warningMeters.length === 0) return [];

    let offlineCount = 0;
    let lateCount = 0;
    let processingCount = 0;
    let configCount = 0;
    let otherCount = 0;

    warningMeters.forEach(m => {
      const statuses = (m.channels || []).map(ch => (ch.currentStatus || '').trim().toLowerCase());
      const meterStatus = (m.status || '').toLowerCase();
      const isOff = statuses.some(s => s === 'offline') || meterStatus === 'offline';
      const isLate = statuses.some(s => s === 'late') || meterStatus === 'delayed';
      const isProc = statuses.some(s => s.includes('processing') || s.includes('stuck') || s.includes('import') || s.includes('aggregation') || s.includes('frozen'));
      const isConfig = statuses.some(s => s.includes('unmapped') || s.includes('config') || s.includes('unknown') || s.includes('unavailable'));

      if (isOff) offlineCount++;
      else if (isLate) lateCount++;
      else if (isProc) processingCount++;
      else if (isConfig) configCount++;
      else otherCount++;
    });

    const groups: { label: string; count: number; color: string }[] = [
      { label: 'Offline', count: offlineCount, color: '#c2410c' },
      { label: 'Late', count: lateCount, color: '#d97706' },
      { label: 'Processing', count: processingCount, color: '#64748b' },
      { label: 'Config', count: configCount, color: '#7c3aed' }
    ];

    if (otherCount > 0) {
      groups.push({ label: 'Other', count: otherCount, color: '#94a3b8' });
    }

    const filtered = groups.filter(g => g.count > 0);
    const max = Math.max(...filtered.map(g => g.count), 1);

    return filtered.map(g => ({
      label: g.label,
      count: g.count,
      pctOfMax: Math.max(8, (g.count / max) * 100),
      color: g.color
    }));
  }

  get healthyLivePercentage(): number {
    if (this.totalCount <= 0) return 0;
    return Math.min(100, Math.max(0, Math.round((this.healthyCount / this.totalCount) * 100)));
  }

  getMaintenanceStaticBreakdown(): { name: string; shortName: string; count: number; heightPct: number; color: string }[] {
    const allMeters = this.getCustomerMeters(this.allLoggers);
    const staticMeters = allMeters.filter(m => m.isStaticSection);

    const siteCategories = new Set<string>();
    allMeters.forEach(m => {
      const type = (m.type || m.categoryName || '').trim();
      if (type) siteCategories.add(type.charAt(0).toUpperCase() + type.slice(1).toLowerCase());
    });

    if (siteCategories.size === 0) {
      siteCategories.add('Electricity');
      siteCategories.add('Gas');
      siteCategories.add('Water');
    }

    const staticCatMap = new Map<string, number>();
    staticMeters.forEach(m => {
      const type = (m.type || m.categoryName || 'General').trim();
      const norm = type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
      staticCatMap.set(norm, (staticCatMap.get(norm) || 0) + 1);
      siteCategories.add(norm);
    });

    const catList = Array.from(siteCategories).slice(0, 4);
    const maxCount = Math.max(...catList.map(c => staticCatMap.get(c) || 0), 1);

    const getShortName = (name: string): string => {
      const low = name.toLowerCase();
      if (low.includes('elec')) return 'Elec';
      if (low.includes('gas')) return 'Gas';
      if (low.includes('water')) return 'Water';
      if (low.includes('solar')) return 'Solar';
      if (low.includes('heat')) return 'Heat';
      if (low.includes('oil')) return 'Oil';
      return name.substring(0, 4);
    };

    return catList.map(cat => {
      const count = staticCatMap.get(cat) || 0;
      const heightPct = count > 0 ? Math.max(30, (count / maxCount) * 100) : 0;
      return {
        name: cat,
        shortName: getShortName(cat),
        count: count,
        heightPct: heightPct,
        color: '#7c3aed'
      };
    });
  }

  isReviewEligible(logger: CustomerMeterRow): boolean {
    return this.isSuperAdmin &&
           this.activeTab === 'MAINTENANCE' &&
           this.currentSiteId > 0 &&
           !this.isGlobalView &&
           logger.canReviewMonitoringSource === true &&
           logger.isCalculatedMeasurement !== true &&
           logger.reviewLogicalGroupId !== undefined &&
           (logger.monitoringSourceType === undefined || logger.monitoringSourceType === null || logger.monitoringSourceType === MonitoringSourceType.Unknown) &&
           logger.status === 'Status Unavailable';
  }

  isDerivedUnavailable(logger: CustomerMeterRow): boolean {
    return this.isSuperAdmin &&
           this.activeTab === 'MAINTENANCE' &&
           this.currentSiteId > 0 &&
           !this.isGlobalView &&
           logger.status === 'Status Unavailable' &&
           logger.isCalculatedMeasurement === true;
  }

  toggleReviewDropdown(logger: CustomerMeterRow): void {
    if (this.activeDropdownLogger === logger) {
      this.activeDropdownLogger = null;
    } else {
      this.activeDropdownLogger = logger;
    }
  }

  selectAction(action: string, logger: CustomerMeterRow): void {
    this.activeDropdownLogger = null;
    if (action === 'STATIC') {
      if (!this.isReviewEligible(logger)) {
        return;
      }
      this.activeReviewLogger = logger;
      this.reviewError = null;
      this.savingReview = false;
    }
  }

  cancelReview(): void {
    this.activeReviewLogger = null;
    this.savingReview = false;
    this.reviewError = null;
  }

  confirmReview(): void {
    if (!this.activeReviewLogger || this.savingReview) return;

    if (!this.isReviewEligible(this.activeReviewLogger)) {
      this.reviewError = 'This measurement is not eligible for monitoring source review.';
      return;
    }

    const lgId = this.activeReviewLogger.reviewLogicalGroupId;
    if (!lgId) {
      this.reviewError = 'Logical Group identifier is missing for this measurement.';
      return;
    }

    this.savingReview = true;
    this.reviewError = null;
    this.cdr.markForCheck();

    this.siteService.markMonitoringSourceStatic$({
      siteId: this.currentSiteId,
      logicalGroupId: lgId
    }).subscribe({
      next: () => {
        this.savingReview = false;
        this.activeReviewLogger = null;
        this.successMessage = 'Measurement successfully marked as Static / Bill-Based.';
        this.loadData();
        setTimeout(() => {
          this.successMessage = null;
          this.cdr.markForCheck();
        }, 5000);
      },
      error: (err) => {
        this.savingReview = false;
        this.reviewError = 'An error occurred while saving the monitoring source. Please verify your permissions and try again.';
        this.cdr.markForCheck();
      }
    });
  }

  hasReviewableContributors(logger: CustomerMeterRow): boolean {
    return logger.reviewableContributorLogicalGroupIds !== undefined &&
           logger.reviewableContributorLogicalGroupIds.length > 0;
  }

  filterByContributors(logger: CustomerMeterRow): void {
    this.activeDropdownLogger = null;
    this.activeContributorFilterIds = logger.reviewableContributorLogicalGroupIds || [];
    this.activeContributorParentName = logger.configuredName || logger.meterName || 'Combined Measurement';
    this.applyFilters();
  }

  clearContributorFilter(): void {
    this.activeContributorFilterIds = null;
    this.activeContributorParentName = '';
    this.applyFilters();
  }

  close(): void {
    this.dialogRef.close();
  }
}
